import { Metrics } from '@newrelic/browser-agent/features/metrics';
import { PageAction } from '@newrelic/browser-agent/features/page_action';
import { PageViewEvent } from '@newrelic/browser-agent/features/page_view_event';
import { PageViewTiming } from '@newrelic/browser-agent/features/page_view_timing';
import { MicroAgent } from '@newrelic/browser-agent/loaders/micro-agent';
import { IPromiseResolveGeneric } from 'library/ts/interfaces/IPromiseResolveGeneric';
import { ILogger } from 'library/ts/utilities/logger/interfaces/ILogger';
import { LogService } from 'library/ts/utilities/logger/LogService';
import { Utilities } from 'library/ts/utilities/Utilities';
import { ISmartTagRuntimeVO } from 'tag/ts/smart_tag/interfaces/ISmartTagRuntimeVO';
import { INewRelicAttributes } from 'tag/ts/smart_tag/interfaces/utilities/INewRelicAttributes';
import { INewRelicManager } from 'tag/ts/smart_tag/interfaces/utilities/INewRelicManager';
import { EmbedType } from 'tag/ts/tag/model/type/embed/EmbedType';

declare global {
	interface Window {
		newrelic: MicroAgent;
	}
}

type AttributeValue = string | number | null;

/**
 * @copyright © Paramount Streaming
 * @author Todd Alexander
 * @description NewRelic browser monitoring singleton
 */
export class NewRelicManager implements INewRelicManager {
	private static _instance: INewRelicManager = new NewRelicManager();

	#microAgent: MicroAgent;
	#logger: ILogger;
	#initialized: boolean = false;
	#attributes: INewRelicAttributes;
	#version: string;

	constructor() {
		if ( NewRelicManager._instance ) {
			throw new Error(
				this.name +
				' is a Singleton and can only be accessed through ' +
				this.name +
				'.getInstance()'
			);
		}
		NewRelicManager._instance = this;
		this.#logger = LogService.create( { prefixes: [ 'SmartTag:', this.name + ':' ] } );
	}

	/**
	 * @description Name of this class
	 */
	get name(): string {
		return 'NewRelicManager';
	}

	/**
	 * @description Reference to global Utilities instance
	 * @return {Utilities | undefined}
	 */
	get utils(): Utilities | undefined {
		return window?.PARA?.PlayerServices?.Utilities;
	}

	public static getInstance() {
		return NewRelicManager._instance;
	}

	/**
	 * @description return SmartTag player attributes for categorizing performance metrics
	 * @return {INewRelicAttributes}
	 */
	get attributes(): INewRelicAttributes {
		return this.#attributes;
	}

	/**
	 * @description settter for SmartTag player attributes
	 * @param {INewRelicAttributes} attributesObj
	 */
	set attributes( attributesObj: INewRelicAttributes ) {
		this.#attributes = attributesObj;
	}

	/**
	 * @description checks if New Relic has already been initialized and new relic namespace already exists in browser
	 * @returns {boolean}
	 */
	#newRelicNamespaceExists = (): boolean => {
		return this.#initialized === true && !this.utils.Type.isNullOrUndefined( window?.newrelic );
	};

	/**
	 * @description Sets a custom attribute on the New Relic browser agent.
	 * Can be called at any time after initialization to add or update attributes.
	 * @param {string} name - The attribute name
	 * @param {string|number|null} value - The attribute value
	 */
	setCustomAttribute = ( name: string, value: AttributeValue ) => {
		if ( this.#newRelicNamespaceExists() && this.#attributes?.embedType === EmbedType.DESKTOP && this.utils.Type.isFunction( window.newrelic.setCustomAttribute ) && !this.utils.Type.isNullOrUndefined( value ) ) {
			try {
				window?.newrelic?.setCustomAttribute( name, value );
				// Also update internal attributes object to keep it in sync
				if ( this.#attributes ) {
					this.#attributes[ name ] = value;
				}
			} catch ( error ) {
				this.#logger.warn( 'setCustomAttribute:', error );
			}
		}
	};

	/**
	 * @description adds all SmartTag custom attributes on global browser agent
	 */
	addAttributes = () => {
		if ( !this.utils.Type.isNullOrUndefined( this.attributes ) ) {
			for ( const key in this.#attributes ) {
				if ( !this.utils.Type.isNullOrUndefined( this.#attributes[ key ] ) ) {
					this.setCustomAttribute( key, this.#attributes[ key ] );
				}
			}
		}
	};

	/**
	 * @description ads Page Action event to New Relic instance for Performance Metric logging purposes
	 * @param {string} name
	 * @param {string|number|null} value
	 */
	#buildPayload( value: AttributeValue ): Record<string, AttributeValue> {
		const payload: Record<string, AttributeValue> = { value: value };
		if ( !this.utils.Type.isNullOrUndefined( this.attributes ) ) {
			for ( const key in this.attributes ) {
				if ( !this.utils.Type.isNullOrUndefined( this.attributes[ key ] ) ) {
					payload[ key ] = this.attributes[ key ];
				}
			}
		}
		return payload;
	}

	addPageAction = ( name: string, value: AttributeValue ) => {
		if ( this.#newRelicNamespaceExists() && this.utils.Type.isFunction( this.#microAgent.addPageAction ) && !this.utils.Type.isNullOrUndefined( value ) ) {
			try {
				this.#microAgent?.addPageAction( name, this.#buildPayload( value ) );
			} catch ( error ) {
				this.#logger.warn( 'addPageAction:', error );
			}
		}
	};

	/**
	 * @description Adds release information to New Relic instance in micro and global agents
	 */
	addRelease = () => {
		// adds release info to New Relic microagent
		if ( this.#newRelicNamespaceExists() && this.utils.Type.isFunction( this.#microAgent.addRelease ) && !this.utils.Type.isNullOrUndefined( this.#version ) ) {
			try {
				this.#microAgent?.addRelease( 'SmartTag', this.#version );
			} catch ( error ) {
				this.#logger.warn( 'addRelease:', error );
			}
		}
		// also adds SmartTag release info to global browser agent on desktop devices
		if ( this.#newRelicNamespaceExists() && this.#attributes?.embedType === EmbedType.DESKTOP && this.utils.Type.isFunction( window.newrelic.addRelease ) && !this.utils.Type.isNullOrUndefined( this.#version ) ) {
			try {
				window?.newrelic?.addRelease( 'SmartTag', this.#version );
			} catch ( error ) {
				this.#logger.warn( 'addRelease:', error );
			}
		}
	};

	/**
	 * @description sets application version to New Relic instance in micro and global agents
	 */
	setApplicationVersion = () => {
		// adds application version to New Relic microagent
		if ( this.#newRelicNamespaceExists() && this.utils.Type.isFunction( this.#microAgent.setApplicationVersion ) && !this.utils.Type.isNullOrUndefined( this.#version ) ) {
			try {
				this.#microAgent?.setApplicationVersion( this.#version );
			} catch ( error ) {
				this.#logger.warn( 'setApplicationVersion:', error );
			}
		}
		// also adds SmartTag application version info to global browser agent on desktop devices
		if ( this.#newRelicNamespaceExists() && this.#attributes.embedType === EmbedType.DESKTOP && this.utils.Type.isFunction( window.newrelic.setApplicationVersion ) && !this.utils.Type.isNullOrUndefined( this.#version ) ) {
			try {
				window?.newrelic?.setApplicationVersion( this.#version );
			} catch ( error ) {
				this.#logger.warn( 'setApplicationVersion:', error );
			}
		}
	};

	/**
	 * @description Initializes New Relic instance via the Micro Agent loader
	 * @return {Promise<IPromiseResolveGeneric>}
	 */
	#initNewRelic = (): Promise<IPromiseResolveGeneric> => {
		const options = {
			info: {
				beacon: 'bam.nr-data.net',
				errorBeacon: 'bam.nr-data.net',
				licenseKey: 'NRJS-b5dcb3a7b0855a31fdd',
				applicationID: '1103285613',
				sa: 1
			},
			loader_config: {
				accountID: '2936348',
				trustKey: '2321606',
				agentID: '1103285613',
				licenseKey: 'NRJS-b5dcb3a7b0855a31fdd',
				applicationID: '1103285613'
			},
			init: {
				ajax: { deny_list: [ 'bam.nr-data.net' ], enabled: false },
				distributed_tracing: { enabled: false },
				jserrors: { enabled: true },
				privacy: { cookies_enabled: false },
				session_replay: { enabled: false },
				session_trace: { enabled: false },
				spa: { enabled: false }
			}
		};

		return new Promise(
			( resolve, reject ) => {
				try {

					this.#logger.log( 'initialize', 'New Relic' );
					this.#microAgent = new MicroAgent( {
						...options,
						features: [
							Metrics,
							PageAction,
							PageViewEvent,
							PageViewTiming
						]
					} );
					this.utils.Delay.delay().then(
						() => {
							resolve( { msg: 'New Relic agent initialized', success: true } );
						}
					);

				} catch ( error ) {
					reject( { msg: 'New Relic agent initialization failed:', success: false, result: error } );
				}
			}
		);
	};

	initialize = ( runtimeVO: ISmartTagRuntimeVO = window?.SmartTag?.runtimeVO, version: string = window?.SmartTag?.info?.version ): Promise<IPromiseResolveGeneric> => {
		this.#version = version;
		return new Promise(
			( resolve, reject ) => {
				if ( this.#initialized === true ) {
					this.#logger.log( 'initialize', 'NewRelic is disabled or already initialized' );
					resolve( { msg: 'NewRelic is disabled or already initialized', success: true, result: this } );
				} else if ( this.#initialized === false && this.utils.Convert.stringToBoolean( runtimeVO?.readyCallback.newRelic ) === true ) {
					this.#initialized = true;

					this.#initNewRelic().then(
						( data ) => {
							resolve( { msg: 'NewRelic startup process', success: true, result: this } );
						}
					).catch(
						( error ) => {
							reject( { msg: 'NewRelic startup process failed:', sucess: false, result: error } );
						}
					);
				} else {
					resolve( { msg: 'NewRelic startup process skipped', success: true, result: this } );
				}
			}
		);
	};
}